import React from 'react';
import { Link } from 'react-router-dom';

type ButtonBaseProps = {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  children: React.ReactNode;
  className?: string;
  as?: React.ElementType;
  to?: string;
  href?: string;
};

type ButtonProps = ButtonBaseProps & 
  (React.ButtonHTMLAttributes<HTMLButtonElement> | React.AnchorHTMLAttributes<HTMLAnchorElement>);

export const Button: React.FC<ButtonProps> = ({ 
  variant = 'primary', 
  size = 'md', 
  fullWidth = false,
  children, 
  className = '',
  as: Component = 'button',
  to,
  href,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed';
  
  const variantStyles = {
    primary: 'bg-black text-white hover:bg-gray-900 shadow-sm hover:shadow-md focus:ring-gray-800',
    secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-800',
    outline: 'border-2 border-black text-black hover:bg-gray-100 focus:ring-gray-800',
    ghost: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:ring-gray-500',
  };
  
  const sizeStyles = {
    sm: 'text-sm px-3 py-1.5 gap-1.5',
    md: 'text-base px-4 py-2 gap-2',
    lg: 'text-lg px-6 py-3 gap-2.5',
  };
  
  const widthStyle = fullWidth ? 'w-full' : '';
  
  const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyle} ${className || ''}`;
  
  if (Component === Link && to) {
    return (
      <Component to={to} className={combinedClassName} {...props}>
        {children}
      </Component>
    );
  }
  
  if (Component === 'a' && href) {
    const anchorProps = { href, ...props } as React.AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <Component className={combinedClassName} {...anchorProps}>
        {children}
      </Component>
    );
  }
  
  const buttonProps = { ...props } as React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <Component className={combinedClassName} {...buttonProps}>
      {children}
    </Component>
  );
};